import { useState } from 'react';
import {
  ArrowRight,
  ChevronLeft,
  Flame,
  Loader2,
  Tractor,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useStoredAudienceId } from '../../../hooks/useActiveAudience';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { useComparateurStore } from './useComparateurStore';
import { StepEyebrow } from './StepEyebrow';
import type { DossierEnergyInput } from '@shared/schemas/comparateur';
import { cn } from '../../../lib/cn';

interface EnergyOption {
  id: DossierEnergyInput['energyChoice'];
  icon: LucideIcon;
  label: string;
  description: string;
  accent: 'brand' | 'accent' | 'lavender';
}

const OPTIONS: readonly EnergyOption[] = [
  {
    id: 'electricity',
    icon: Zap,
    label: 'Électricité',
    description:
      "Comparaison des offres marché libre et tarif réglementé Pro. Idéal si votre site est tout électrique.",
    accent: 'accent',
  },
  {
    id: 'gas',
    icon: Flame,
    label: 'Gaz naturel',
    description:
      'Comparaison des offres gaz : prix fixe vs prix indexé sur PEG. Pour chaudières, restauration, process industriels.',
    accent: 'lavender',
  },
  {
    id: 'both',
    icon: Zap,
    label: 'Électricité + Gaz',
    description:
      "Vous avez un contrat sur les deux énergies ? On compare l'offre duale ou les meilleures offres séparées (souvent moins cher).",
    accent: 'brand',
  },
];

/** Hint affiché pour le profil exploitation agricole (TURPE B/J) */
function AgriTurpeHint() {
  const audience = useStoredAudienceId();
  if (audience !== 'agriculteurs') return null;
  return (
    <div className="mb-5 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-50/60 px-4 py-3 text-sm text-accent-900">
      <Tractor className="mt-0.5 h-4 w-4 shrink-0 text-accent-600" />
      <div>
        <p className="font-medium">Exploitation agricole — comparaison adaptée à votre compteur</p>
        <p className="mt-0.5 text-accent-700">
          Votre profil exploitation est pré-rempli. On comparera les offres du marché adaptées à
          la puissance de votre compteur (petite ou moyenne puissance).
        </p>
      </div>
    </div>
  );
}

// NB : classes complètes (pas d'interpolation `focus-visible:${…}`) pour que
// Tailwind les détecte à la compilation.
const ACCENT_STYLES = {
  brand: {
    iconBg: 'bg-brand-100 text-brand-700',
    selectedBg: 'border-brand-400 bg-brand-50/70',
    focusRing: 'focus-visible:ring-brand-300',
  },
  accent: {
    iconBg: 'bg-accent-100 text-accent-700',
    selectedBg: 'border-accent-400 bg-accent-50/70',
    focusRing: 'focus-visible:ring-accent-300',
  },
  lavender: {
    iconBg: 'bg-lavender-100 text-lavender-700',
    selectedBg: 'border-lavender-400 bg-lavender-50/70',
    focusRing: 'focus-visible:ring-lavender-300',
  },
} as const;

export function StepEnergyType() {
  const store = useComparateurStore();
  const initial = store.dossier?.energyChoice ?? null;
  const [selected, setSelected] = useState<DossierEnergyInput['energyChoice'] | null>(initial);

  async function onContinue() {
    if (!selected) return;
    try {
      await store.saveEnergy({ energyChoice: selected });
    } catch {
      /* error handled by store */
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => store.goBack()}
        className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Retour
      </button>

      <StepEyebrow step="energy" />
      <h3 className="mt-2 h-section">
        Quelle énergie souhaitez-vous comparer ?
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Vous pourrez modifier ce choix plus tard. Nous sommes courtier indépendant des fournisseurs
        — vous accédez à toutes les offres du marché, sans partenariat exclusif.
      </p>

      <AgriTurpeHint />

      {store.error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[12px]">{store.error}</AlertDescription>
        </Alert>
      )}

      <fieldset className="mt-6 border-0 p-0 m-0">
        <legend className="sr-only">Quelle énergie souhaitez-vous comparer ?</legend>
        <div role="radiogroup" aria-label="Énergie à comparer" className="space-y-3">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const styles = ACCENT_STYLES[opt.accent];
          const isSelected = selected === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => setSelected(opt.id)}
              className={cn(
                'group flex w-full items-start gap-4 rounded-xl border-2 p-4 text-left transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                isSelected
                  ? `${styles.selectedBg} shadow-[0_4px_16px_rgba(16,185,129,0.08)] ${styles.focusRing}`
                  : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50 focus-visible:ring-brand-400',
              )}
            >
              <span
                className={cn(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-150 ease-out group-hover:scale-105',
                  styles.iconBg,
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={1.7} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-[14px] font-semibold text-ink">
                  {opt.label}
                  {opt.id === 'both' && (
                    <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] text-brand-700">
                      Populaire
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">
                  {opt.description}
                </p>
              </div>
              <span
                className={cn(
                  'mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                  isSelected ? 'border-brand-500 bg-brand-500' : 'border-stone-300 bg-white',
                )}
              >
                {isSelected && (
                  <svg className="h-3 w-3 text-white" viewBox="0 0 12 10" fill="none">
                    <path
                      d="M1 5l3.5 3.5L11 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            </button>
          );
        })}
        </div>
      </fieldset>

      <Button
        type="button"
        variant="brand"
        size="xl"
        onClick={onContinue}
        disabled={!selected || store.loading}
        className="mt-6 w-full gap-2"
      >
        {store.loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Enregistrement…
          </>
        ) : (
          <>
            Continuer
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>
    </div>
  );
}
